<template>
  <div>
    <div @click="showAsyncPicker" :light="true" class="wmq-cascade" :class="colorClass">
      <div class="display-style" style="font-size: 14px">{{ showLabel ? showLabel : placeholder }}</div>
      <div v-if="showLabel && !disabled" class="sprite icon-close wmq-cascade-clear" style="width: 10%;" @click="reset"></div>
    </div>
    <van-popup v-model="show" round position="bottom">
      <!-- 搜索框 -->
      <div class="search-wrapper">
        <van-search
          v-model="searchText"
          placeholder="搜索关键词"
          show-action
          @clear="onClearSearch"
        >
          <template #action>
            <div @click="onClearSearch">清空</div>
          </template>
        </van-search>
      </div>

      <!-- 级联选择器 -->
      <van-cascader
        v-if="filteredData.length > 0"
        v-model="modelValue"
        :title="placeholder"
        :options="filteredData"
        @close="show = false"
        @finish="onFinish"
        :field-names="{text: 'label', value: 'code', children: 'children'}"
      />
      <van-empty v-else description="暂无搜索结果" :image-size="80" />
    </van-popup>
  </div>
</template>

<script>
import request from '@/utils/request'
import { isEmpty } from 'lodash'
export default {
  name: 'Cascade',
  props: {
    value: {
      type: String,
      default() {
        return undefined
      }
    },
    fontSizeClass: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    optionSource: {
      type: Object,
      default() {
        return {}
      }
    },
    options: {
      type: Array,
      default() {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      asyncData: [],
      selectedText: [],
      show: false,
      modelValue: '',
      selectedOptions: [],
      searchText: '' // 搜索关键词
    }
  },
  computed: {
    colorClass() {
      const val = this.value
      const isEmpty = val == null || val === '' || (Array.isArray(val) && val.length === 0)
      return isEmpty ? 'color-gray' : 'color-black'
    },
    fontClass() {
      return this.fontSizeClass ? 'font-bold' : ''
    },
    showLabel() {
      return this.selectedText.join('/')
    },
    // 过滤后的数据
    filteredData() {
      if (!this.searchText || this.searchText.trim() === '') {
        return this.asyncData
      }
      return this.filterTreeData(this.asyncData, this.searchText.trim())
    }
  },
  mounted () {
    if (!isEmpty(this.optionSource)) {
      request({
        url: this.optionSource.path,
        method: 'get'
      }).then(res => {
        // 清理空 children 数组后再赋值
        this.asyncData = this.cleanEmptyChildren(res.data.items)
      })
    } else {
      // 对静态 options 也进行清理
      this.asyncData = this.cleanEmptyChildren(this.options)
    }
  },
  watch: {
    value() {
      if (!this.value) {
        this.selectedText = []
        this.modelValue = null
      }
    }
  },
  methods: {
    /**
     * 递归清理空 children 数组
     * 级联选择器要求：叶子节点不应有 children 属性或 children 为 undefined
     * 但后端可能返回 children: []，导致组件误判为还有下级
     */
    cleanEmptyChildren(data) {
      if (!data) {
        return data
      }

      if (Array.isArray(data)) {
        return data.map(item => this.cleanEmptyChildren(item))
      }

      if (typeof data === 'object') {
        const cleaned = { ...data }

        // 如果存在 children 属性
        if (Array.isArray(cleaned.children)) {
          if (cleaned.children.length === 0) {
            // 空数组：删除 children 属性
            delete cleaned.children
          } else {
            // 非空数组：递归清理子节点
            cleaned.children = this.cleanEmptyChildren(cleaned.children)
          }
        }

        return cleaned
      }

      return data
    },

    /**
     * 递归过滤树形数据
     * @param {Array} data - 原始树形数据
     * @param {String} keyword - 搜索关键词
     * @returns {Array} 过滤后的树形数据
     */
    filterTreeData(data, keyword) {
      if (!data || !Array.isArray(data)) {
        return []
      }

      const result = []
      const lowerKeyword = keyword.toLowerCase()

      data.forEach(item => {
        // 检查当前节点是否匹配
        const labelMatch = item.label && item.label.toLowerCase().includes(lowerKeyword)
        const codeMatch = item.code && item.code.toLowerCase().includes(lowerKeyword)

        // 递归过滤子节点
        let filteredChildren = null
        if (item.children && item.children.length > 0) {
          filteredChildren = this.filterTreeData(item.children, keyword)
        }

        // 如果当前节点匹配 或 子节点有匹配，则保留该节点
        if (labelMatch || codeMatch || (filteredChildren && filteredChildren.length > 0)) {
          const newItem = { ...item }

          if (filteredChildren && filteredChildren.length > 0) {
            newItem.children = filteredChildren
          } else {
            // 如果是叶子节点匹配，删除 children 属性
            delete newItem.children
          }

          result.push(newItem)
        }
      })

      return result
    },

    /**
     * 清空搜索
     */
    onClearSearch() {
      this.searchText = ''
    },

    reset(e) {
      this.selectedText = []
      this.modelValue = null
      this.selectedOptions = []
      this.$emit('updateValue', undefined)
      e.stopPropagation()
    },
    onFinish({ selectedOptions }) {
      this.show = false
      this.selectedText = selectedOptions.map((option) => option.label)
      this.selectedOptions = selectedOptions
      this.$emit('updateValue', this.modelValue)
    },
    showAsyncPicker() {
      if (this.disabled) {
        return
      }
      this.searchText = '' // 打开弹窗时清空搜索
      this.show = true
    },
    setSelectedText(data) {
      this.selectedText = data
    }
  }
}
</script>

<style lang="stylus" scope>
.search-wrapper
    background-color: #fff
    padding: 8px 16px 0
    border-bottom: 1px solid #eee

.wmq-cascade{
    text-align: left;
    font-size: 28px;
    background-color: #fff;
    border: 1px solid #eee;
    width: 100%;
    display: flex;
    position: relative;
    min-height: 40px;
    height: auto;
    padding: 8px 10px;
    align-items: center;
}
.wmq-cascade-clear{
    height: 42px;
    width: 42px;
    line-height: 60px;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}
.display-style{
    white-space: normal;
    word-break: break-all;
    line-height: 1.5;
    width: 90%;
}
.color-gray{
    color: #64748b !important
}
.color-black{
    color: #1e293b !important
}
.font-bold{
    font-size: 24px;
}
.cube-validator_invalid .van-popup {
  color: #666 !important
}
</style>
